import { createContext, useContext, useState } from "react";

const ExperimentContext = createContext();

/**
 * * Wrapper that provides experiment variant context
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {Object} ExperimentContextProvider
 */
export function ExperimentContextProvider({ children }) {
  /**
   * * EXPERIMENT
   * @param {Object} experimentId
   * @param {string} experimentId.title
   * @param {string} experimentId.description
   * @param {string[]} experimentId.variants
   * @param {string} experimentId.currentVariant
   */
  const [experiments, setExperiments] = useState({});

  const setCurrentVariant = (experimentId, variant) => {
    setExperiments((experiments) => ({
      ...experiments,
      [experimentId]: {
        ...experiments[experimentId],
        currentVariant: variant,
      },
    }));
  };

  return (
    <ExperimentContext.Provider value={{ experiments, setCurrentVariant }}>
      {children}
    </ExperimentContext.Provider>
  );
}

/**
 * * Hook to access experiment variant context
 * @param {string} experimentId
 * @returns {Object} { variants, currentVariant }
 */
export function useExperimentContext(experimentId) {
  const context = useContext(ExperimentContext);
  if (!context) {
    throw new Error(
      "useExperimentContext must be used within a ExperimentContextProvider"
    );
  }

  const { experiments, setCurrentVariant } = context;
  if (!experimentId) {
    return { experiments, setCurrentVariant };
  }

  const demo = experiments[experimentId];
  if (!demo) {
    throw new Error(`Demo ${experimentId} does not exist`);
  }
  const variants = demo.variants;
  const currentVariant = demo.currentVariant;
  return { variants, currentVariant };
}
